import type { createDocumentServiceClient, DocumentFolder } from "../documents/client";

// The one Health Vault folder every Order Medicines prescription upload is filed under, instead
// of Health Vault root. Reuses document-service's existing folder contract unchanged (GET
// /v1/folders, POST /v1/folders) -- there is no medicine-order-specific folder concept, S3 path,
// or authorization rule; this is an ordinary Health Vault folder like any a user could create
// themselves, just found-or-created automatically on the caller's behalf.
export const MEDICINE_ORDERS_FOLDER_NAME = "Medicine Orders";

type FolderClient = Pick<ReturnType<typeof createDocumentServiceClient>, "createFolder" | "listFolders">;

function findRootFolderByName(folders: DocumentFolder[], name: string): string | null {
  const existing = folders.find((folder) => folder.status === "active" && !folder.parent_folder_id && folder.name === name);
  return existing?.folder_id ?? null;
}

/**
 * Resolves the folder_id for MEDICINE_ORDERS_FOLDER_NAME, creating it once if it doesn't exist yet
 * -- never creating a duplicate. Best-effort: any failure here (including losing a create race to
 * document-service's own folder-name-conflict check, or a transient network error) falls back to
 * `null` (Health Vault root, exactly where prescriptions landed before this folder existed) rather
 * than blocking the order.
 */
export async function resolveMedicineOrdersFolderId(client: FolderClient): Promise<string | null> {
  try {
    const existing = findRootFolderByName(await client.listFolders(), MEDICINE_ORDERS_FOLDER_NAME);
    if (existing) {
      return existing;
    }

    try {
      const created = await client.createFolder(MEDICINE_ORDERS_FOLDER_NAME, null);
      return created.folder_id;
    } catch {
      // Most likely lost a race to create the same folder concurrently -- document-service itself
      // rejects a duplicate name at the same level (folder_name_conflict), so look it up again
      // instead of treating that as a hard failure.
      return findRootFolderByName(await client.listFolders(), MEDICINE_ORDERS_FOLDER_NAME);
    }
  } catch {
    return null;
  }
}
